/**
 * WebsiteBouwPage — voor klanten zonder website of die redesign nodig hebben.
 *
 * Flow:
 *  1. Check huidige redesign-verdict (POST /api/ai/classify-redesign)
 *  2. Toon "Geen website? Bouw blueprint" CTA → /api/ai/website-blueprint
 *  3. Toon resultaat: sitemap, content blocks, design direction
 *
 *  Voor managed klanten: koppeling met IMPERIA AI executie team.
 *  Voor self-service: download handoff pack.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Sparkles, Globe, AlertTriangle, CheckCircle, Crown,
  ArrowRight, FileText, Layout, RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const VERDICT_STYLE = {
  no_website: { color: '#dc2626', bg: '#fef2f2', icon: AlertTriangle, label: 'Geen website' },
  redesign_required: { color: '#D4A017', bg: '#fffbeb', icon: AlertTriangle, label: 'Redesign nodig' },
  major_improvements: { color: '#D4A017', bg: '#fff8e1', icon: AlertTriangle, label: 'Grote verbeteringen' },
  minor_improvements: { color: '#2563eb', bg: '#eff6ff', icon: Sparkles, label: 'Kleine verbeteringen' },
  ok: { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle, label: 'Website is OK' },
};

export default function WebsiteBouwPage() {
  const { api, user } = useAuth();
  const [verdict, setVerdict] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [v, b, d] = await Promise.all([
          api('get', '/api/ai/classify-redesign').catch(() => ({ data: {} })),
          api('get', '/api/ai/website-blueprint').catch(() => ({ data: {} })),
          api('get', '/api/platform/dashboard').catch(() => ({ data: {} })),
        ]);
        setVerdict(v.data?.verdict ? v.data : null);
        setBlueprint(b.data?.blueprint ? b.data : null);
        setScan(d.data?.scan || null);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const classify = async () => {
    setClassifying(true);
    try {
      const r = await api('post', '/api/ai/classify-redesign');
      setVerdict(r.data);
      toast.success('Analyse compleet');
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setClassifying(false);
  };

  const generateBlueprint = async () => {
    if (!scan?.company_name) {
      toast.error('Vul eerst uw bedrijfsgegevens in (BedrijfsInfo)');
      return;
    }
    setBuilding(true);
    try {
      const r = await api('post', '/api/ai/website-blueprint', {
        company: scan.company_name,
        branche: scan.branche || 'algemeen',
        locatie: scan.locatie || 'Nederland',
        services: scan.services || [],
        target_audience: 'MKB en consumenten',
      });
      setBlueprint(r.data);
      toast.success('Blueprint gegenereerd!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBuilding(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden...</div>;

  const v = verdict?.verdict ? VERDICT_STYLE[verdict.verdict] || VERDICT_STYLE.ok : null;
  const VerdictIcon = v?.icon || Globe;
  const showBlueprintCTA = !blueprint && (!verdict || ['no_website', 'redesign_required', 'major_improvements'].includes(verdict.verdict));

  return (
    <div data-testid="website-bouw-page">
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
          <Globe size={18} style={{ display: 'inline', color: '#D4A017', marginRight: 8, verticalAlign: 'middle' }} />
          Website verbeteren · redesign · nieuwe site
        </h1>
        <p style={{ color: '#64748b', fontSize: 13 }}>
          Onze AI bepaalt of u updates, een redesign of een volledig nieuwe website nodig heeft.
        </p>
      </div>

      {/* Verdict card */}
      {verdict ? (
        <div data-testid="redesign-verdict" style={{
          background: v.bg, border: `1px solid ${v.color}33`,
          borderRadius: 14, padding: 18, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: v.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <VerdictIcon size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 10, color: v.color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                Verdict
              </p>
              <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                {verdict.label || v.label}
              </p>
              {verdict.reasoning && (
                <p style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
                  {verdict.reasoning}
                </p>
              )}
              {verdict.issues && verdict.issues.length > 0 && (
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {verdict.issues.slice(0, 5).map((iss, i) => (
                    <li key={i} style={{ fontSize: 11, color: '#475569', marginBottom: 3 }}>{iss}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #f1f5f9', padding: 20, textAlign: 'center', marginBottom: 16 }}>
          <Sparkles size={28} style={{ color: '#D4A017', marginBottom: 10 }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Nog geen verdict</p>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
            Klik op de knop om uw site door onze AI te laten beoordelen.
          </p>
          <button
            data-testid="classify-btn"
            onClick={classify}
            disabled={classifying}
            style={{ background: '#0f172a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            {classifying ? 'Analyseren...' : 'Analyseer mijn website'}
          </button>
        </div>
      )}

      {/* Blueprint CTA */}
      {showBlueprintCTA && (
        <div data-testid="blueprint-cta" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          borderRadius: 14, padding: 22, marginBottom: 16, color: 'white',
        }}>
          <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>
            <Layout size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} /> Bouw mijn website blueprint
          </p>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
            Geen website of tijd voor een redesign?
          </p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 14, maxWidth: 600 }}>
            Onze AI genereert een complete blueprint: sitemap, content blocks per pagina, design direction en SEO opzet.
            U krijgt een implementation-ready pack — bouw het zelf óf laat IMPERIA AI het uitvoeren (Managed AI Bot).
          </p>
          <button
            data-testid="generate-blueprint-btn"
            onClick={generateBlueprint}
            disabled={building}
            style={{ background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {building ? 'Genereren...' : 'Bouw mijn blueprint'} {!building && <ArrowRight size={14} />}
          </button>
        </div>
      )}

      {/* Blueprint result */}
      {blueprint?.blueprint && (
        <div data-testid="blueprint-result" style={{ background: 'white', borderRadius: 14, border: '1px solid #f1f5f9', padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
              <FileText size={15} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle', color: '#D4A017' }} />
              Uw website blueprint
            </p>
            <button
              data-testid="regenerate-blueprint-btn"
              onClick={generateBlueprint}
              disabled={building}
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <RefreshCw size={11} /> Opnieuw genereren
            </button>
          </div>

          {/* Sitemap */}
          {blueprint.blueprint.sitemap && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>Sitemap</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {blueprint.blueprint.sitemap.map((page, i) => (
                  <span key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px', fontSize: 11, color: '#0f172a', fontWeight: 600 }}>
                    {typeof page === 'string' ? page : page.title || page.slug || page.url || 'Pagina'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pages content */}
          {blueprint.blueprint.pages && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>Pagina inhoud</p>
              {blueprint.blueprint.pages.slice(0, 8).map((page, i) => (
                <details key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                  <summary style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', cursor: 'pointer' }}>
                    {page.title || page.slug || `Pagina ${i + 1}`}
                  </summary>
                  <div style={{ marginTop: 8, fontSize: 11, color: '#475569', lineHeight: 1.55 }}>
                    {page.h1 && <p><strong>H1:</strong> {page.h1}</p>}
                    {page.meta_description && <p style={{ marginTop: 4 }}><strong>Meta:</strong> {page.meta_description}</p>}
                    {page.sections && (
                      <ul style={{ marginTop: 6, paddingLeft: 16 }}>
                        {page.sections.map((s, j) => (
                          <li key={j} style={{ marginBottom: 3 }}>{typeof s === 'string' ? s : s.heading || s.title}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}

          {/* Design direction */}
          {blueprint.blueprint.design_direction && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 }}>Design direction</p>
              <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.55 }}>{typeof blueprint.blueprint.design_direction === 'string' ? blueprint.blueprint.design_direction : JSON.stringify(blueprint.blueprint.design_direction)}</p>
            </div>
          )}

          {/* Managed CTA */}
          {user?.role !== 'beheer_paid' && user?.role !== 'admin' && (
            <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #fde68a', borderRadius: 10, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <div>
                <p style={{ fontSize: 12, fontWeight: 800, color: '#92400e' }}>
                  <Crown size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                  Laat IMPERIA AI uw website bouwen
                </p>
                <p style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                  Managed AI Bot vanaf €995,95 excl. btw eerste maand (daarna €2.495,95/mnd excl. btw)
                </p>
              </div>
              <Link to="/platform/checkout?plan=imperium_beheer_launch" data-testid="upgrade-to-managed-btn" style={{ background: '#D4A017', color: '#0f172a', textDecoration: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
                Upgrade naar Managed →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
